/**
 * Derived-signal helpers and cascades. Pure-ish functions
 * over the `derivedSignals` atom: validity, the plottable pseudo-columns derived
 * signals expose, and the rename/remove cascades that keep everything that
 * references a derived name consistent.
 *
 * The blob cascaded a derived rename/remove into both `plot.state`
 * (y_left/y_right/color_by/fits/custom_colors) AND the filter rows. Both halves
 * now exist: the filter side is here, the plot side goes through
 * `state/plot-state.ts`. `scheduleDerivedReplot` debounces a `doPlot()` (§10.8
 * seam - a no-op until the renderer lands, matching the blob, which only
 * replots when a chart is already on screen).
 */

import { derivedSignals, filters, type DerivedSignal } from '../../state/store.ts';
import { allColumns } from '../../state/store.ts';
import { stripDerivedFromPlot, renameDerivedInPlot } from '../../state/plot-state.ts';
import { doPlot } from '../plot/plot-actions.ts';
import { isNumericType } from '../../utils/columns.ts';
import { DERIVED_DIALECT, validateNumberFormula } from '../plot/number-formula.ts';
import type { ColumnInfo } from '../../api/types.ts';

/** Mirror of the backend name regex in models.py - change both together. */
export const DERIVED_NAME_RE = /^[A-Za-z_][A-Za-z0-9_ ]{0,63}$/;

/** A fresh derived row: a name and an empty formula. */
export function newDerivedSignal(existing: DerivedSignal[]): DerivedSignal {
  const taken = new Set(existing.map((d) => d.name));
  let n = existing.length + 1;
  while (taken.has(`Derived ${n}`)) n++;
  return {
    name: `Derived ${n}`,
    op: '*',
    operand_a: null,
    operand_a_const: null,
    operand_b_col: null,
    operand_b_const: null,
    expr: '',
  };
}

/**
 * The formula a row reads as. Rows from before formulas (a URL, a saved chart or
 * the assistant's `A op B` shape) are shown as the equivalent formula; the first
 * edit stores it in `expr`, which the backend prefers over the operand fields.
 */
export function derivedFormula(d: DerivedSignal): string {
  if (d.expr != null) return d.expr;
  const operand = (col: string | null | undefined, n: number | null | undefined): string =>
    col ? JSON.stringify(col) : n != null && Number.isFinite(Number(n)) ? String(n) : '';
  const a = operand(d.operand_a, d.operand_a_const);
  const b = operand(d.operand_b_col, d.operand_b_const);
  // A half-built row keeps its dangling operator so it reads as unfinished.
  return a || b ? `${a} ${d.op} ${b}`.trim() : '';
}

/**
 * Whether a derived row is complete enough to compute: a valid, non-shadowing
 * name and a formula the derived dialect accepts. `rawColumnNames` is the set of
 * real column names the derived must not shadow; `numericNames` the signals the
 * formula may use (omit to check the shape only).
 */
export function isDerivedValid(
  d: DerivedSignal, rawColumnNames: Set<string>, numericNames?: Set<string>,
): boolean {
  if (!d || !d.name || !DERIVED_NAME_RE.test(d.name)) return false;
  if (rawColumnNames.has(d.name)) return false;
  const known = numericNames ? (name: string) => numericNames.has(name) : undefined;
  return validateNumberFormula(derivedFormula(d), known, DERIVED_DIALECT) === null;
}

/** Whether a name is acceptable for a derived signal at index `idx`. */
export function isDerivedNameValid(
  name: string,
  idx: number,
  list: DerivedSignal[],
  rawColumnNames: Set<string>,
): boolean {
  if (!DERIVED_NAME_RE.test(name)) return false;
  if (rawColumnNames.has(name)) return false; // can't shadow a raw column
  return !list.some((o, i) => i !== idx && o.name === name); // no dup
}

/** The derived signals exposed as plottable pseudo-columns (`DOUBLE`). */
export function derivedAsColumns(list: DerivedSignal[]): ColumnInfo[] {
  return list.map((d) => ({
    name: d.name,
    type: 'DOUBLE',
    null_pct: 0,
    null_count: 0,
    total_count: 0,
    months: [],
    derived: true,
  }));
}

/** Raw columns + derived pseudo-columns - the pool for filter/plot pickers. */
export function plottableColumns(): ColumnInfo[] {
  return allColumns.value.concat(derivedAsColumns(derivedSignals.value));
}

/** Whether `name` is the name of a current derived signal. */
export function isDerivedName(name: string): boolean {
  return derivedSignals.value.some((d) => d.name === name);
}

/** True if `name` should be treated as numeric for filter operators. */
export function isNumericColumnName(name: string): boolean {
  if (isDerivedName(name)) return true;
  const col = allColumns.value.find((c) => c.name === name);
  return !!col && isNumericType(col.type);
}

/**
 * Drop every reference to a removed derived name. Filters lose matching rows;
 * the plot axes/color/fits cascade is a §10.7/§10.8 seam (those atoms don't
 * exist yet).
 */
export function removeDerivedReferences(name: string): void {
  const kept = filters.value.filter((r) => r.column !== name);
  if (kept.length !== filters.value.length) filters.set(kept);
  stripDerivedFromPlot(name); // x/y axes, color, histogram, fits, custom colors
}

/** Rewrite every reference from `oldName` to `newName` after a rename. */
export function renameDerivedReferences(oldName: string, newName: string): void {
  if (oldName === newName) return;
  const updated = filters.value.map((r) =>
    r.column === oldName ? { ...r, column: newName } : r,
  );
  // `map` always returns a new array; only notify if a row actually changed.
  if (updated.some((r, i) => r !== filters.value[i])) filters.set(updated);
  renameDerivedInPlot(oldName, newName); // x/y axes, color, histogram, fits, custom colors
}

let replotTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Debounced replot after a derived edit (blob `scheduleDerivedReplot`, 250ms).
 * `doPlot` is itself a §10.8 seam (no-op until the renderer lands), but the
 * debounce + call site are wired so nothing changes when §10.8 arrives.
 */
export function scheduleDerivedReplot(): void {
  if (replotTimer) clearTimeout(replotTimer);
  replotTimer = setTimeout(() => {
    replotTimer = null;
    doPlot();
  }, 250);
}
